import React from "react";
import { push } from "store/actions/router";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { get, isEmpty } from "lodash";
import { formatCurrency } from "utils/number";
import { accountTypesToEnglishMapping } from "statics/accountTypes";

import Button from "components/Button";
import IconHeader from "components/IconHeader";
import Alert from "components/Alert";
import SelectBox from "../../../../signUp/individual/SelectBox";
import type { RootState } from "types/state";

const mapStateToProps = (state: RootState) => {
  const iraAccounts = state.accounts.iraAccounts;

  const accountsWithBalance = iraAccounts.filter(
    (account) => get(account, "balances.totalAccountBalance", 0) !== 0
  );

  const accounts = accountsWithBalance;

  return {
    accounts,
  };
};

const mapDispatchToProps = { push };

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface OwnProps {
  error?: string;
  isFetching?: boolean;
  accountId?: string;
  goToNamedStep?: (stepName: string) => void;
  onAccountSelect?: (accountId: string) => void;
  // react-step-wizard injects stepName at runtime; the workflow declares it
  // on the JSX element.
  stepName?: string;
}

type Props = WithApolloClient<OwnProps & PropsFromRedux>;

interface State {
  selectedAccountType: string;
}

class DistributionAccountType extends React.PureComponent<Props, State> {
  constructor() {
    // preserved verbatim: original calls super()/constructor() with no args
    // even though React passes props — behavior unchanged on purpose.
    // @ts-expect-error original JS omits the props argument to super()
    super();
    this.state = {
      selectedAccountType: "",
    };
  }

  _setSelectedAccountType = (accountId: string) => {
    this.setState({ selectedAccountType: accountId });

    if (this.props.onAccountSelect) {
      this.props.onAccountSelect(accountId);
    }
  };

  render() {
    const { accounts, error, goToNamedStep } = this.props;

    const articleStyle = {
      paddingBottom: 0,
    };

    return (
      <div id="account-selection">
        <div className="mega-container">
          <div className="step-container is-active" data-circle-percent="100">
            <section className="page-title-wrap">
              <article className="text-cell">
                <IconHeader
                  variant="pageHeader"
                  headerText="Which account would you like to withdrawal funds from?"
                />
              </article>
            </section>
            <div>
              <section
                className="form-sec-2col"
                style={{ paddingTop: 20, paddingBottom: 20 }}
              >
                <article className="col-form" style={articleStyle}>
                  <div className="account-types">
                    {accounts.map((account) => {
                      return (
                        <SelectBox
                          label={
                            accountTypesToEnglishMapping[
                              account.accountType as keyof typeof accountTypesToEnglishMapping
                            ]
                          }
                          // PRESERVED BUG (conversion-only): original derefs
                          // account.balances with `.`, throwing if balances is
                          // null/undefined — kept via `!`.
                          description={`${formatCurrency(
                            account.balances!.totalAccountBalance!
                          )}`}
                          key={account.id}
                          displayChevron={true}
                          onSelect={() => {
                            this._setSelectedAccountType(account.id as string);
                          }}
                          isSelected={
                            this.state.selectedAccountType === account.id
                          }
                        />
                      );
                    })}
                  </div>
                </article>
              </section>
              <section className="form-sec-2col">
                {error && <Alert type="error" msg={error} />}
                <div className="submit-row btn-row">
                  {/* PRESERVED BUG (conversion-only): goToNamedStep is an optional prop the original calls without a guard — kept via `!` so it still throws if the parent omits it. Button `name` is required in ButtonProps but optional in practice (spread to <button name={undefined}>); original markup omitted it. */}
                  {/* @ts-expect-error preserved missing required `name` prop */}
                  <Button
                    onClick={() => goToNamedStep!("addressConfirmation")}
                    btnLabel="Go Back"
                    color="cancel"
                  />
                  <Button
                    style={{ marginLeft: 10 }}
                    name="action"
                    disabled={isEmpty(this.state.selectedAccountType)}
                    onClick={() => goToNamedStep!("distributionAmount")}
                  />
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default connector(withApollo<Props>(DistributionAccountType));
